'use client';
import React, { useState } from 'react';
import { ArrowUp, ArrowDown, Minus, Flame, Search, ChevronUp, ChevronDown, Trophy } from 'lucide-react';
import { USERS, getRankMovement, formatNumber } from '@/lib/mockData';
import type { User } from '@/lib/mockData';

const TIME_FILTERS = [
  { id: 'filter-today', label: 'Today' },
  { id: 'filter-week', label: 'This Week' },
  { id: 'filter-month', label: 'This Month' },
  { id: 'filter-alltime', label: 'All Time' },
];

const SORT_OPTIONS = [
  { id: 'sort-score', label: 'Overall Score', key: 'score' as keyof User },
  { id: 'sort-messages', label: 'Most Messages', key: 'messages' as keyof User },
  { id: 'sort-active', label: 'Most Active', key: 'activeDays' as keyof User },
  { id: 'sort-groups', label: 'Most Groups', key: 'groups' as keyof User },
  { id: 'sort-streak', label: 'Longest Streak', key: 'streak' as keyof User },
  { id: 'sort-growth', label: 'Biggest Growth', key: 'weeklyGrowth' as keyof User },
];

export default function LeaderboardTable() {
  const [timeFilter, setTimeFilter] = useState('filter-alltime');
  const [sortKey, setSortKey] = useState<keyof User>('score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');

  // Backend integration point: fetch /api/leaderboard?period=alltime&sort=score&dir=desc&q=search
  const filtered = USERS
    .filter((u) =>
      u.displayName.toLowerCase().includes(search.toLowerCase()) ||
      u.username.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const aVal = a[sortKey] as number;
      const bVal = b[sortKey] as number;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });

  const handleSort = (key: keyof User) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ col }: { col: keyof User }) => {
    if (sortKey !== col) return <ChevronUp size={12} style={{ opacity: 0.3 }} />;
    return sortDir === 'desc' ? <ChevronDown size={12} style={{ color: 'var(--primary)' }} /> : <ChevronUp size={12} style={{ color: 'var(--primary)' }} />;
  };

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl transition-all duration-200 focus:outline-none focus:ring-1"
            style={{
              background: 'var(--muted)',
              border: '1px solid var(--border)',
              color: 'var(--foreground)',
              focusRingColor: 'var(--primary)',
            }}
          />
        </div>

        {/* Time filters */}
        <div
          className="flex items-center gap-1 p-1 rounded-xl overflow-x-auto scrollbar-hide"
          style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
        >
          {TIME_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setTimeFilter(f.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-200 flex-shrink-0"
              style={{
                background: timeFilter === f.id ? 'var(--primary)' : 'transparent',
                color: timeFilter === f.id ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sort chips */}
      <div className="flex items-center gap-2 mb-5 overflow-x-auto scrollbar-hide pb-1">
        <span className="text-xs font-semibold flex-shrink-0" style={{ color: 'var(--muted-foreground)' }}>
          Sort by:
        </span>
        {SORT_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            onClick={() => handleSort(opt.key)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all duration-200 flex-shrink-0"
            style={{
              background: sortKey === opt.key ? 'rgba(34,197,94,0.12)' : 'var(--muted)',
              color: sortKey === opt.key ? 'var(--primary)' : 'var(--muted-foreground)',
              border: `1px solid ${sortKey === opt.key ? 'rgba(34,197,94,0.3)' : 'var(--border)'}`,
            }}
          >
            {opt.label}
            <SortIcon col={opt.key} />
          </button>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto rounded-xl" style={{ border: '1px solid var(--border)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide w-16" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                Rank
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                User
              </th>
              <th
                className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide cursor-pointer hover:opacity-80 transition-opacity"
                style={{ color: sortKey === 'score' ? 'var(--primary)' : 'var(--muted-foreground)', letterSpacing: '0.06em' }}
                onClick={() => handleSort('score')}
              >
                <span className="flex items-center justify-end gap-1">Score <SortIcon col="score" /></span>
              </th>
              <th
                className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide cursor-pointer hover:opacity-80 transition-opacity"
                style={{ color: sortKey === 'messages' ? 'var(--primary)' : 'var(--muted-foreground)', letterSpacing: '0.06em' }}
                onClick={() => handleSort('messages')}
              >
                <span className="flex items-center justify-end gap-1">Messages <SortIcon col="messages" /></span>
              </th>
              <th
                className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide cursor-pointer hover:opacity-80 transition-opacity"
                style={{ color: sortKey === 'activeDays' ? 'var(--primary)' : 'var(--muted-foreground)', letterSpacing: '0.06em' }}
                onClick={() => handleSort('activeDays')}
              >
                <span className="flex items-center justify-end gap-1">Active Days <SortIcon col="activeDays" /></span>
              </th>
              <th
                className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide cursor-pointer hover:opacity-80 transition-opacity"
                style={{ color: sortKey === 'groups' ? 'var(--primary)' : 'var(--muted-foreground)', letterSpacing: '0.06em' }}
                onClick={() => handleSort('groups')}
              >
                <span className="flex items-center justify-end gap-1">Groups <SortIcon col="groups" /></span>
              </th>
              <th
                className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide cursor-pointer hover:opacity-80 transition-opacity"
                style={{ color: sortKey === 'streak' ? 'var(--primary)' : 'var(--muted-foreground)', letterSpacing: '0.06em' }}
                onClick={() => handleSort('streak')}
              >
                <span className="flex items-center justify-end gap-1">Streak <SortIcon col="streak" /></span>
              </th>
              <th
                className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide cursor-pointer hover:opacity-80 transition-opacity"
                style={{ color: sortKey === 'weeklyGrowth' ? 'var(--primary)' : 'var(--muted-foreground)', letterSpacing: '0.06em' }}
                onClick={() => handleSort('weeklyGrowth')}
              >
                <span className="flex items-center justify-end gap-1">Growth <SortIcon col="weeklyGrowth" /></span>
              </th>
              <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                Move
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-16">
                  <div className="flex flex-col items-center gap-2">
                    <Trophy size={32} style={{ color: 'var(--muted-foreground)', opacity: 0.4 }} />
                    <p className="font-semibold" style={{ color: 'var(--muted-foreground)' }}>
                      No users match your search
                    </p>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)', opacity: 0.6 }}>
                      Try a different name or username
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((user, idx) => {
                const movement = getRankMovement(user);
                const isTopThree = user.rank <= 3;

                return (
                  <tr
                    key={`row-${user.id}`}
                    className="transition-all duration-150 group cursor-pointer"
                    style={{
                      borderBottom: '1px solid var(--border)',
                      background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.background = 'rgba(34,197,94,0.04)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLTableRowElement).style.background = idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)';
                    }}
                  >
                    {/* Rank */}
                    <td className="px-4 py-3.5 w-16">
                      <div className="flex items-center gap-2">
                        {user.rank === 1 ? (
                          <span className="text-lg">🥇</span>
                        ) : user.rank === 2 ? (
                          <span className="text-lg">🥈</span>
                        ) : user.rank === 3 ? (
                          <span className="text-lg">🥉</span>
                        ) : (
                          <span
                            className="text-sm font-bold w-7 h-7 rounded-lg flex items-center justify-center text-tabular"
                            style={{
                              background: 'var(--muted)',
                              color: 'var(--muted-foreground)',
                            }}
                          >
                            {user.rank}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* User */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                          style={{
                            background: user.avatarColor,
                            color: '#fff',
                            boxShadow: isTopThree
                              ? user.rank === 1
                                ? '0 0 0 2px #f59e0b'
                                : user.rank === 2
                                ? '0 0 0 2px #94a3b8' :'0 0 0 2px #cd7c3a' :'none',
                          }}
                        >
                          {user.initials}
                        </div>
                        <div>
                          <p className="font-bold text-sm" style={{ color: 'var(--foreground)' }}>
                            {user.displayName}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Score */}
                    <td className="px-4 py-3.5 text-right">
                      <span className="font-bold text-tabular" style={{ color: 'var(--foreground)' }}>
                        {formatNumber(user.score)}
                      </span>
                    </td>

                    {/* Messages */}
                    <td className="px-4 py-3.5 text-right">
                      <span className="text-tabular font-medium" style={{ color: 'var(--foreground)' }}>
                        {formatNumber(user.messages)}
                      </span>
                    </td>

                    {/* Active Days */}
                    <td className="px-4 py-3.5 text-right">
                      <span className="text-tabular font-medium" style={{ color: 'var(--foreground)' }}>
                        {user.activeDays}
                      </span>
                    </td>

                    {/* Groups */}
                    <td className="px-4 py-3.5 text-right">
                      <span className="text-tabular font-medium" style={{ color: 'var(--foreground)' }}>
                        {user.groups}
                      </span>
                    </td>

                    {/* Streak */}
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Flame size={13} style={{ color: '#f97316' }} />
                        <span className="font-bold text-tabular" style={{ color: '#f97316' }}>
                          {user.streak}d
                        </span>
                      </div>
                    </td>

                    {/* Weekly Growth */}
                    <td className="px-4 py-3.5 text-right">
                      <span
                        className="font-bold text-tabular text-xs px-2 py-1 rounded-lg"
                        style={{
                          background: user.weeklyGrowth > 0 ? 'rgba(34,197,94,0.1)' : user.weeklyGrowth < 0 ? 'rgba(239,68,68,0.1)' : 'var(--muted)',
                          color: user.weeklyGrowth > 0 ? 'var(--primary)' : user.weeklyGrowth < 0 ? '#ef4444' : 'var(--muted-foreground)',
                        }}
                      >
                        {user.weeklyGrowth > 0 ? '+' : ''}{user.weeklyGrowth}%
                      </span>
                    </td>

                    {/* Movement */}
                    <td className="px-4 py-3.5 text-right">
                      <span
                        className="flex items-center justify-end gap-0.5 font-bold text-xs"
                        style={{
                          color: movement.direction === 'up' ? 'var(--primary)' : movement.direction === 'down' ? '#ef4444' : 'var(--muted-foreground)',
                        }}
                      >
                        {movement.direction === 'up' ? (
                          <ArrowUp size={12} />
                        ) : movement.direction === 'down' ? (
                          <ArrowDown size={12} />
                        ) : (
                          <Minus size={12} />
                        )}
                        {movement.label}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {filtered.map((user) => {
          const movement = getRankMovement(user);
          return (
            <div
              key={`mobile-row-${user.id}`}
              className="card-glass p-4 transition-all duration-200 hover:scale-[1.01]"
            >
              <div className="flex items-center gap-3 mb-3">
                {/* Rank */}
                <div className="flex-shrink-0">
                  {user.rank === 1 ? (
                    <span className="text-2xl">🥇</span>
                  ) : user.rank === 2 ? (
                    <span className="text-2xl">🥈</span>
                  ) : user.rank === 3 ? (
                    <span className="text-2xl">🥉</span>
                  ) : (
                    <span
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-tabular"
                      style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
                    >
                      {user.rank}
                    </span>
                  )}
                </div>

                {/* Avatar */}
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ background: user.avatarColor, color: '#fff' }}
                >
                  {user.initials}
                </div>

                {/* Name */}
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate" style={{ color: 'var(--foreground)' }}>
                    {user.displayName}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    {user.username}
                  </p>
                </div>

                {/* Movement */}
                <span
                  className="flex items-center gap-0.5 font-bold text-sm"
                  style={{
                    color: movement.direction === 'up' ? 'var(--primary)' : movement.direction === 'down' ? '#ef4444' : 'var(--muted-foreground)',
                  }}
                >
                  {movement.direction === 'up' ? <ArrowUp size={14} /> : movement.direction === 'down' ? <ArrowDown size={14} /> : <Minus size={14} />}
                  {movement.label}
                </span>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: 'Score', value: formatNumber(user.score) },
                  { label: 'Msgs', value: formatNumber(user.messages) },
                  { label: 'Groups', value: user.groups.toString() },
                  { label: 'Streak', value: `${user.streak}d`, color: '#f97316' },
                ].map((stat) => (
                  <div key={`mobile-stat-${user.id}-${stat.label}`} className="text-center">
                    <p className="text-xs font-semibold text-tabular" style={{ color: stat.color || 'var(--foreground)' }}>
                      {stat.value}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between mt-6 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          Showing {filtered.length} of {USERS.length} users
        </p>
        <div className="flex items-center gap-1">
          {[1, 2, 3].map((page) => (
            <button
              key={`page-${page}`}
              className="w-8 h-8 rounded-lg text-xs font-semibold transition-all duration-200"
              style={{
                background: page === 1 ? 'var(--primary)' : 'var(--muted)',
                color: page === 1 ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
              }}
            >
              {page}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}